import { useMemo } from 'react';

import flatMap from 'lodash/flatMap';

import { getStep } from '../../ResourceUtilizationSection/_hooks/useChartsData/_utils';
import { ResourceUtilizationChartType } from '../../ResourceUtilizationSection/types';
import { useClusterDashboardTimeRangeSelection } from '../../useClusterDashboardTimeRangeSelection';
import { OomKillBucketWorkload } from '../types';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { useWorkloadEventMetricsQuery } from '@cast/shared-cost-data-access';

const MANAGED_BY_WOOP = 'woop';

export type OomKillBucketQueryRange = {
  getRange: () => { from: string; to: string };
  key: string;
  stepSeconds: number;
};

type Params = {
  bucketTimestamp?: string;
  managed: boolean;
  enabled?: boolean;
  /** When omitted, falls back to console cluster dashboard time range. */
  rangeSelection?: OomKillBucketQueryRange;
};

export const useOomKillBucketWorkloadsQuery = ({
  bucketTimestamp,
  managed,
  enabled = true,
  rangeSelection,
}: Params) => {
  const { cluster } = useCluster();
  const consoleTimeRange = useClusterDashboardTimeRangeSelection();

  const getRange = rangeSelection?.getRange ?? consoleTimeRange.getRange;
  const key = rangeSelection?.key ?? consoleTimeRange.key;
  const range = getRange();
  const stepSeconds =
    rangeSelection?.stepSeconds ??
    getStep(range, ResourceUtilizationChartType.AREA);

  const query = useWorkloadEventMetricsQuery({
    clusterId: cluster.id,
    startTime: range.from,
    endTime: range.to,
    eventTypes: ['OOMKilled'],
    stepSeconds,
    groupByManagedBy: true,
    bucketTimestamp,
    key: `${key}-bucket-${bucketTimestamp ?? ''}-${
      managed ? 'managed' : 'unmanaged'
    }`,
    enabled: enabled && !!bucketTimestamp,
  });

  const workloads = useMemo((): OomKillBucketWorkload[] => {
    const series = (query.data ?? []).filter((s) => {
      if (s.eventType !== 'OOMKilled') {
        return false;
      }
      const isManaged = s.managedBy === MANAGED_BY_WOOP;
      return managed ? isManaged : !isManaged;
    });

    return flatMap(series, (s) =>
      flatMap(s.items ?? [], (item) =>
        (item.byWorkload ?? []).map((workload): OomKillBucketWorkload => {
          // workloadId is on the live API but not yet in generated OpenAPI types.
          const workloadId = (workload as { workloadId?: string }).workloadId;

          return {
            workloadName: workload.workloadName,
            workloadType: workload.workloadType,
            namespace: workload.namespace,
            container: workload.container,
            eventCount: workload.eventCount,
            ...(workloadId ? { workloadId } : {}),
            timestamp: item.timestamp,
          };
        })
      )
    );
  }, [query.data, managed]);

  return {
    ...query,
    workloads,
  };
};
